/**
 * Text Fitting Utility
 * Calculates optimal font sizes and breaks text into lines
 */

/**
 * @typedef {Object} TextConstraints
 * @property {number} maxLines - Maximum number of lines
 * @property {number} maxCharsPerLine - Approximate max characters per line
 * @property {Object} fontSizeRange - Font size range
 * @property {number} fontSizeRange.min - Minimum font size
 * @property {number} fontSizeRange.max - Maximum font size
 * @property {number} width - Container width in pixels
 * @property {number} height - Container height in pixels
 */

/**
 * @typedef {Object} TextFitResult
 * @property {string} text - Original text
 * @property {number} fontSize - Calculated optimal font size
 * @property {string[]} lines - Text broken into lines
 * @property {boolean} needsRewrite - Whether text needs to be shortened
 * @property {string} [rewriteReason] - Reason for rewrite if needed
 */

/**
 * Break text into lines that fit within width
 * @param {string} text - Text to break
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxLines - Maximum number of lines
 * @param {CanvasRenderingContext2D} ctx - Canvas context for measurement
 * @returns {string[]}
 */
function breakTextIntoLines(text, maxWidth, maxLines, ctx) {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = ctx.measureText(testLine).width;

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;

      if (lines.length >= maxLines) {
        break;
      }
    }
  }

  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Measure text width using canvas
 * @param {string} text - Text to measure
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @returns {number} Width in pixels
 */
function measureTextWidth(text, ctx) {
  return ctx.measureText(text).width;
}

/**
 * Fit text to constraints by calculating optimal font size
 * @param {string} text - Text to fit
 * @param {TextConstraints} constraints - Container and text constraints
 * @param {string} fontFamily - Font family to use
 * @param {number} lineHeight - Line height multiplier (default 1.2)
 * @returns {TextFitResult}
 */
export function fitTextToConstraints(
  text,
  constraints,
  fontFamily = "Inter",
  lineHeight = 1.2
) {
  if (!text || !constraints) {
    return {
      text: text || "",
      fontSize: constraints?.fontSizeRange?.min || 12,
      lines: [],
      needsRewrite: true,
      rewriteReason: "No text or constraints provided"
    };
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Try different font sizes from max to min
  for (
    let fontSize = constraints.fontSizeRange.max;
    fontSize >= constraints.fontSizeRange.min;
    fontSize -= 2
  ) {
    ctx.font = `${fontSize}px ${fontFamily}`;

    // Try to break text into lines
    const lines = breakTextIntoLines(
      text,
      constraints.width,
      constraints.maxLines,
      ctx
    );

    // Check if text fits within constraints
    const totalHeight = lines.length * fontSize * lineHeight;
    const fitsVertically = totalHeight <= constraints.height;
    const fitsHorizontally = lines.every(
      (line) => measureTextWidth(line, ctx) <= constraints.width
    );
    const fitsLineCount = lines.length <= constraints.maxLines;

    if (fitsVertically && fitsHorizontally && fitsLineCount) {
      return {
        text,
        fontSize,
        lines,
        needsRewrite: false
      };
    }
  }

  // If we get here, text doesn't fit even at minimum font size
  const estimatedMaxChars = constraints.maxCharsPerLine * constraints.maxLines;

  return {
    text,
    fontSize: constraints.fontSizeRange.min,
    lines: [],
    needsRewrite: true,
    rewriteReason: `Text too long. Should be under ${estimatedMaxChars} characters (currently ${text.length}).`
  };
}

/**
 * Estimate if text will fit constraints without full calculation
 * @param {string} text - Text to check
 * @param {TextConstraints} constraints - Constraints
 * @returns {boolean}
 */
export function estimateTextFit(text, constraints) {
  const estimatedMaxChars = constraints.maxCharsPerLine * constraints.maxLines;

  // Quick check: if text is way over character limit, it won't fit
  if (text.length > estimatedMaxChars * 1.2) {
    return false;
  }

  // Quick check: if text is comfortably under, it should fit
  if (text.length < estimatedMaxChars * 0.8) {
    return true;
  }

  // Middle ground - might fit, needs full calculation
  return null; // null means "needs full calculation"
}

/**
 * Calculate optimal font size for a single line of text
 * @param {string} text - Text to fit
 * @param {number} maxWidth - Maximum width
 * @param {number} maxFontSize - Maximum font size
 * @param {number} minFontSize - Minimum font size
 * @param {string} fontFamily - Font family
 * @returns {number} Optimal font size
 */
export function calculateSingleLineFontSize(
  text,
  maxWidth,
  maxFontSize,
  minFontSize,
  fontFamily = "Inter"
) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  for (let fontSize = maxFontSize; fontSize >= minFontSize; fontSize -= 2) {
    ctx.font = `${fontSize}px ${fontFamily}`;
    const width = measureTextWidth(text, ctx);

    if (width <= maxWidth) {
      return fontSize;
    }
  }

  return minFontSize;
}
